import React from "react"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import customParseFormat from "dayjs/plugin/customParseFormat"
import timezone from "dayjs/plugin/timezone"
import { useDispatch } from "react-redux"
import TextField from "@mui/material/TextField"
import InputAdornment from "@mui/material/InputAdornment"
import FormControlLabel from "@mui/material/FormControlLabel"
import Checkbox from "@mui/material/Checkbox"
import Card from "@mui/material/Card"
import Grid from "@mui/material/Grid"
import CardActions from "@mui/material/CardActions"
import Button from "@mui/material/Button"
import Add from "@mui/icons-material/Add"
import Dialog from "@mui/material/Dialog"
import DialogContent from "@mui/material/DialogContent"
import DialogActions from "@mui/material/DialogActions"
import { Alert, CardContent, CardHeader } from "@mui/material"
import { useTheme } from "@mui/material/styles"
import {
  ref,
  get,
  query,
  orderByChild,
  startAt,
  endAt,
  onValue,
} from "firebase/database"

import { sdk9db } from "../../../firebase"
import { dateAndTimeToTimestamp, formatDate } from "../../../utils/dateUtils"
import { setSnackbar } from "../../../actions/globalUiActions"
import ButtonWithProgress from "../../template-parts/ButtonWithProgress"
import StudentAutocomplete from "./StudentAutocomplete"
import LessonCancelForm from "./LessonCancelForm"
import { logError } from "../../../utils/errors"
import { millisToMinutes } from "../../../utils/misc"
import { updateLesson } from "../../../actions/bookingActions"
import {
  ID,
  InstructorLesson,
  LessonTypeName,
  UserSearch,
} from "../../../types"
import { DEFAULT_TIMEZONE } from "../../../config"
import type { LessonCardState } from "./LessonCard"
import { useSharedLessonStudentContext } from "./SharedLessonStudentProvider"
import LessonTypeSelect from "../../template-parts/LessonTypeSelect"
import { lessonTypes } from "../../../content"

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(customParseFormat)
const TIME_FORMAT = "HH:mm"

type Props = {
  lessonSlot: InstructorLesson
  onStatusChanged?: (status: LessonCardState["formStatus"]) => void
  lessonSlotSavedCallback: (lesson: InstructorLesson) => void
  saveErrorCallback: (error: Error) => void
}

type StudentDetails = {
  [studentId: string]: {
    drivingMinutes: number
    observationMinutes: number
    pickupDetails?: string
    dmvLocation?: string
    allowObserver?: string
  }
}

type StudentLookup = {
  [studentId: string]: {
    displayName: string
  }
}

type FormState = {
  startTime: string
  endTime: string
  price: number
  pickupLocation: string
  isShareable: boolean
  studentDetails: StudentDetails
}

const LessonForm = ({
  lessonSlot,
  onStatusChanged,
  lessonSlotSavedCallback,
  saveErrorCallback,
}: Props) => {
  const dispatch = useDispatch()
  const theme = useTheme()
  const { details } = lessonSlot
  const timeZone = lessonSlot.instructor.timeZone || DEFAULT_TIMEZONE
  const [formState, setFormState] = React.useState<FormState>({
    startTime: formatDate(details.startTime, timeZone, TIME_FORMAT) || "",
    endTime: formatDate(details.endTime, timeZone, TIME_FORMAT) || "",
    price: details.price || 0,
    pickupLocation: details.pickupLocation || "",
    isShareable: details.isShareable || false,
    studentDetails: lessonSlot.students,
  })
  const [studentToRemove, setStudentToRemove] = React.useState<string | null>(
    null,
  )
  const [confirmOpen, setConfirmOpen] = React.useState(false)
  const [addNewOpen, setAddNewOpen] = React.useState(false)
  const [transientSlotExists, setTransientSlotExists] = React.useState(false)
  const [studentSearch, setStudentToAdd] = React.useState<UserSearch | null>(
    null,
  )
  const [isSaving, setIsSaving] = React.useState(false)
  const [studentLookup, setStudentLookup] =
    React.useState<StudentLookup | null>(null)
  const [lessonTypeName, setLessonTypeName] = React.useState<LessonTypeName>(
    lessonSlot.details.lessonType.name,
  )

  const instructorId = lessonSlot.instructor.id
  const lessonId = lessonSlot.id
  const lessonDate = formatDate(details.startTime, timeZone, "date")

  React.useEffect(() => {
    const unsub = onValue(ref(sdk9db, `transientSlots/${lessonId}`), (snap) => {
      setTransientSlotExists(snap.exists())
    })

    return () => {
      unsub()
    }
  }, [lessonId])

  React.useEffect(() => {
    const localLookup: StudentLookup = {}
    const proms = Object.keys(formState.studentDetails).map((studentId) =>
      get(ref(sdk9db, `users/${studentId}`))
        .then((userSnap) => {
          const user = userSnap.val()
          if (!user) {
            throw new Error(`User data not found for ${studentId}`)
          }
          localLookup[studentId] = {
            displayName: user.userName,
          }
        })
        .catch((error) => {
          const message = "Failed to get student."
          dispatch(setSnackbar(message, "error"))
          throw new Error(`${message} - ${error.message}`)
        }),
    )
    Promise.all(proms)
      .then(() => {
        setStudentLookup(localLookup)
      })
      .catch(logError)
  }, [dispatch, formState.studentDetails])

  const handleStudentChange = (
    studentId: string,
    key: string,
    value: string,
    isNumber: boolean,
  ) => {
    const maybeConvertedValue = isNumber ? parseFloat(value) : value
    // TODO This is awful.
    setFormState((prevFormState) => ({
      ...prevFormState,
      studentDetails: {
        ...prevFormState.studentDetails,
        [studentId]: {
          ...prevFormState.studentDetails[studentId],
          [key]: maybeConvertedValue,
        },
      },
    }))
    onStatusChanged?.("dirty")
  }

  const handleChange = (key: string, value: string | boolean | number) => {
    setFormState((prevFormState) => ({
      ...prevFormState,
      [key]: value,
    }))
    onStatusChanged?.("dirty")
  }

  const hasConflict = (
    startTimeMillis: number,
    endTimeMillis: number,
    originalLessonId: string,
  ) => {
    const lessonSlotsQuery = query(
      ref(sdk9db, `lessonSlotsV2/${instructorId}`),
      orderByChild("details/startTime"),
      startAt(startTimeMillis),
      endAt(endTimeMillis),
    )
    return get(lessonSlotsQuery).then((lessonsSnap) => {
      let conflictFound = false
      lessonsSnap.forEach((lessonSnap) => {
        const lesson = lessonSnap.val()
        lesson.id = lessonSnap.key
        if (lesson.id !== originalLessonId) {
          const {
            details: { startTime: thisStartTime, endTime: thisEndTime },
          } = lesson
          if (thisStartTime < endTimeMillis && thisEndTime > startTimeMillis) {
            conflictFound = true
          }
        }
      })
      return conflictFound
    })
  }

  const validateStudentDuration = (
    startTimeMillis: number,
    endTimeMillis: number,
  ) => {
    // Make sure the minutes set for shared students do not exceed
    // the duration of the lesson.
    const durationMinutes = millisToMinutes(endTimeMillis - startTimeMillis)
    const studentDrivingMinutes = Object.keys(formState.studentDetails)
      .map((studentId) => formState.studentDetails[studentId].drivingMinutes)
      .reduce((a, c) => a + c, 0)

    if (formState.isShareable) {
      if (studentDrivingMinutes > durationMinutes) {
        setIsSaving(false)
        throw new Error(
          `Drive minutes must not exceed lesson duration. Got ${studentDrivingMinutes}-minute drive time for ${durationMinutes}-minute lesson.`,
        )
      }
    }
  }

  const getLessonSchedule = () => {
    const dateObject = dayjs.tz(lessonDate, timeZone)

    const startTimeDayjs = dayjs.tz(formState.startTime, TIME_FORMAT, timeZone)
    const endTimeDayjs = dayjs.tz(formState.endTime, TIME_FORMAT, timeZone)
    // Convert date/time to date object.
    const startTimeMillis = dateAndTimeToTimestamp(dateObject, startTimeDayjs)
    const endTimeMillis = dateAndTimeToTimestamp(dateObject, endTimeDayjs)
    const duration = endTimeMillis - startTimeMillis
    const durationMinutes = duration / (1000 * 60)

    return {
      startTimeMillis,
      endTimeMillis,
      durationMinutes,
    }
  }

  const handleSubmit = (e: React.SyntheticEvent) => {
    e.preventDefault()
    setIsSaving(true)

    const { startTimeMillis, endTimeMillis, durationMinutes } =
      getLessonSchedule()

    hasConflict(startTimeMillis, endTimeMillis, lessonSlot.id).then(
      async (doesHaveConflict) => {
        if (doesHaveConflict) {
          setIsSaving(false)
          dispatch(
            setSnackbar(
              "There is already a lesson scheduled at this time, can't save.",
              "error",
            ),
          )
          return
        }

        const convertedPrice = parseFloat(
          formState.price.toString().replace("$", ""),
        )
        const studentsInput = Object.keys(formState.studentDetails).map(
          (studentId) => {
            const {
              pickupDetails,
              drivingMinutes,
              observationMinutes,
              dmvLocation,
              allowObserver,
            } = formState.studentDetails[studentId]

            return {
              id: studentId,
              pickupDetails: pickupDetails || null,
              drivingMinutes: formState.isShareable
                ? drivingMinutes || 0
                : durationMinutes,
              observationMinutes: formState.isShareable
                ? observationMinutes || 0
                : 0,
              allowObserver: allowObserver || "yes",
              ...(lessonTypeName === "dmv-test" && { dmvLocation }),
            }
          },
        )

        try {
          validateStudentDuration(startTimeMillis, endTimeMillis)
        } catch (error: any) {
          dispatch(setSnackbar(error.message, "error"))
          setIsSaving(false)
          return
        }

        const studentsObj = studentsInput.reduce(
          (acc, student) => ({
            ...acc,
            [student.id]: student,
          }),
          {},
        )

        const lessonSlotUpdated = {
          ...lessonSlot,
          details: {
            ...lessonSlot.details,
            lessonType: lessonTypes[lessonTypeName],
            startTime: startTimeMillis,
            endTime: endTimeMillis,
            price: convertedPrice,
            pickupLocation: formState.pickupLocation,
            isShareable: formState.isShareable,
          },
          students: studentsObj,
        }

        Promise.all([
          dispatch(
            updateLesson(instructorId, lessonId, lessonSlot, lessonSlotUpdated),
          ),
        ])
          .then(() => {
            onStatusChanged?.("clean")
            lessonSlotSavedCallback(lessonSlotUpdated)
            setIsSaving(false)
          })
          .catch((error: any) => {
            setIsSaving(false)
            saveErrorCallback(error)
          })
      },
    )
  }

  const addStudentToLesson = ({
    studentId,
    studentName,
    drivingMinutes,
    observationMinutes,
  }: {
    studentId: ID
    studentName: string
    drivingMinutes: number
    observationMinutes: number
  }) => {
    setAddNewOpen(false)
    setFormState((prevFormState) => ({
      ...prevFormState,
      studentDetails: {
        ...prevFormState.studentDetails,
        [studentId]: {
          allowObserver: "yes",
          drivingMinutes,
          observationMinutes,
        },
      },
    }))
    setStudentLookup((prevStudentLookup) => ({
      ...prevStudentLookup,
      [studentId]: {
        displayName: studentName,
      },
    }))
    onStatusChanged?.("dirty")
  }

  const sharedLessonContext = useSharedLessonStudentContext()
  React.useEffect(() => {
    if (!sharedLessonContext) {
      return
    }

    addStudentToLesson({
      studentId: sharedLessonContext.userId,
      studentName: sharedLessonContext.name,
      drivingMinutes: 0,
      observationMinutes: 0,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sharedLessonContext])

  const removeStudentFromLesson = (studentId?: string) => {
    if (studentId) {
      const studentsObj = formState.studentDetails
      delete studentsObj[studentId]
      setFormState((prevFormState) => ({
        ...prevFormState,
        studentDetails: {
          ...studentsObj,
        },
      }))
    }
    setStudentToRemove(null)
    setConfirmOpen(false)
  }

  const numStudents = Object.keys(formState.studentDetails).length
  const numExistingStudents = Object.keys(lessonSlot.students).length

  if (!studentLookup) {
    return null
  }

  return (
    <div>
      <form onSubmit={handleSubmit}>
        <TextField
          label="Start Time"
          type="time"
          name="startTime"
          InputLabelProps={{ shrink: true }}
          inputProps={{ step: 300 }}
          value={formState.startTime}
          onChange={(e) => {
            handleChange("startTime", e.target.value)
          }}
          margin="normal"
          style={{ marginRight: 20 }}
        />
        <TextField
          label="End Time"
          type="time"
          name="endTime"
          InputLabelProps={{ shrink: true }}
          inputProps={{ step: 300 }}
          value={formState.endTime}
          onChange={(e) => {
            handleChange("endTime", e.target.value)
          }}
          margin="normal"
          style={{ marginRight: 20 }}
        />
        <TextField
          label="Price"
          name="price"
          value={formState.price}
          onChange={(e) => {
            handleChange("price", parseFloat(e.target.value))
          }}
          placeholder="130"
          InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
          }}
          margin="normal"
        />
        <TextField
          label="Pickup Location"
          onChange={(e) => {
            handleChange("pickupLocation", e.target.value)
          }}
          value={formState.pickupLocation}
          name="pickupLocation"
          margin="normal"
          fullWidth
        />
        <LessonTypeSelect
          selectedLessonType={lessonTypeName}
          onSelected={setLessonTypeName}
        />
        <br />
        <FormControlLabel
          control={
            <Checkbox
              name="isShareable"
              checked={formState.isShareable}
              onChange={(e) => handleChange("isShareable", e.target.checked)}
              value="isShareable"
              color="primary"
              disabled={numStudents > 1}
            />
          }
          label="Can lesson have more than one student? (Texas & Illinois only)"
        />
        {transientSlotExists && (
          <Alert severity="warning">
            A student is currently self booking for this shared lesson. If they
            do not complete the booking this message will disappear.
          </Alert>
        )}
        <br />
        <Grid container spacing={2} style={{ marginTop: 10 }}>
          {Object.keys(formState.studentDetails).map((studentId) => {
            const { pickupDetails, dmvLocation } =
              formState.studentDetails[studentId]
            const { displayName } = studentLookup[studentId] ?? {}
            const isNewStudent = !lessonSlot.students[studentId]

            return (
              <Grid item key={studentId} md={6} sm={12}>
                <Card
                  elevation={0}
                  style={{
                    marginBottom: 20,
                    border: `2px ${theme.palette.secondary.light} solid`,
                  }}
                  data-cy="student-lesson-card"
                >
                  <CardHeader subheader={displayName} />
                  <CardContent style={{ paddingTop: 0 }}>
                    {formState.isShareable && (
                      <>
                        <TextField
                          type="number"
                          label="Driving Minutes"
                          onChange={(e) => {
                            handleStudentChange(
                              studentId,
                              e.target.name,
                              e.target.value,
                              true,
                            )
                          }}
                          inputProps={{ min: 0 }}
                          value={
                            formState.studentDetails[studentId].drivingMinutes
                          }
                          name="drivingMinutes"
                          margin="normal"
                          style={{ marginRight: 20 }}
                        />
                        <TextField
                          type="number"
                          label="Observation Minutes"
                          onChange={(e) => {
                            handleStudentChange(
                              studentId,
                              e.target.name,
                              e.target.value,
                              true,
                            )
                          }}
                          inputProps={{ min: 0 }}
                          value={
                            formState.studentDetails[studentId]
                              .observationMinutes
                          }
                          name="observationMinutes"
                          margin="normal"
                        />
                        <br />
                      </>
                    )}
                    <TextField
                      label="Special Pickup Instructions"
                      onChange={(e) => {
                        handleStudentChange(
                          studentId,
                          e.target.name,
                          e.target.value,
                          false,
                        )
                      }}
                      value={pickupDetails}
                      name="pickupDetails"
                      margin="normal"
                      fullWidth
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="allowObserver"
                          checked={
                            formState.studentDetails[studentId]
                              .allowObserver === "yes"
                          }
                          onChange={(e) =>
                            handleStudentChange(
                              studentId,
                              e.target.name,
                              e.target.checked ? "yes" : "no",
                              false,
                            )
                          }
                          value="allowObserver"
                          color="primary"
                        />
                      }
                      label="Allow Observer?"
                    />
                    <br />
                    {lessonTypeName === "dmv-test" && (
                      <>
                        <TextField
                          label="DMV Location"
                          onChange={(e) => {
                            handleStudentChange(
                              studentId,
                              e.target.name,
                              e.target.value,
                              false,
                            )
                          }}
                          value={dmvLocation}
                          name="dmvLocation"
                          margin="normal"
                          fullWidth
                        />
                        <br />
                      </>
                    )}
                  </CardContent>
                  <CardActions>
                    <Button
                      variant="outlined"
                      color="secondary"
                      disabled={numExistingStudents <= 1 && !isNewStudent}
                      onClick={() => {
                        if (isNewStudent) {
                          removeStudentFromLesson(studentId)
                        } else {
                          setStudentToRemove(studentId)
                          setConfirmOpen(true)
                        }
                      }}
                    >
                      Remove from lesson {isNewStudent ? "(Cancel)" : ""}
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            )
          })}
          <Grid item md={6} sm={12}>
            {formState.isShareable && numStudents >= 1 ? (
              <Button
                variant="contained"
                color="primary"
                startIcon={<Add />}
                onClick={() => setAddNewOpen(true)}
              >
                Add Student
              </Button>
            ) : null}
          </Grid>
        </Grid>
        <ButtonWithProgress
          variant="contained"
          type="submit"
          color="primary"
          isLoading={isSaving}
          loadingText="Saving..."
          style={{ marginTop: 20 }}
        >
          Save Lesson
        </ButtonWithProgress>
      </form>
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogContent>
          <LessonCancelForm
            lessonId={lessonId}
            instructorId={instructorId}
            studentIdToRemove={studentToRemove}
            onComplete={removeStudentFromLesson}
          />
        </DialogContent>
        <DialogActions>
          <Button color="primary" onClick={() => setConfirmOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={addNewOpen} onClose={() => setAddNewOpen(false)} fullScreen>
        <DialogContent>
          <StudentAutocomplete
            userSelectedCallback={(student) => setStudentToAdd(student)}
            excludeIds={Object.keys(formState.studentDetails)}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              if (!studentSearch) return

              addStudentToLesson({
                studentId: studentSearch.id,
                studentName: studentSearch.name,
                drivingMinutes: 0,
                observationMinutes: 0,
              })
            }}
            style={{ marginTop: 20 }}
            data-cy="add-student-modal-button"
          >
            Add Student
          </Button>
        </DialogContent>
        <DialogActions>
          <Button color="primary" onClick={() => setAddNewOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

export default LessonForm
